import { CheckoutParams, CheckoutSession } from '../../shared/types/checkout';
import {
  Customer,
  CustomerCreateParams,
  PaymentMethod,
} from '../../shared/types/customer';
import {
  Subscription,
  SubscriptionCreateParams,
  SubscriptionUpdateParams,
  SubscriptionStatus,
} from '../../shared/types/subscription';
import { PaymentProvider } from '../../shared/types/provider';
import { PaymentsError, PaymentErrorType } from '../../shared/types/errors';
import { PaymentsConfig } from '../../shared/types/config';
import { BasePaymentProvider } from '../base';

/**
 * A mock payment provider for testing and development purposes.
 * It simulates payment operations without actual API calls.
 */
export class MockPaymentProvider
  extends BasePaymentProvider
  implements PaymentProvider
{
  name = 'MockPaymentProvider';
  private customers: Customer[] = [];
  private subscriptions: Subscription[] = [];
  private nextCustomerId = 1;
  private nextSubscriptionId = 1;

  constructor(config: Omit<PaymentsConfig, 'provider'>) {
    super({ ...config, provider: 'mock' });

    console.log('MockPaymentProvider initialized');
  }

  async initialize(): Promise<void> {
    console.log('MockPaymentProvider initialized');
  }

  async createCheckoutSession(
    params: CheckoutParams
  ): Promise<CheckoutSession> {
    console.log('Mock: createCheckoutSession', params);
    return {
      id: 'mock_cs_123',
      clientSecret: 'mock_client_secret',
      url: 'https://mock-checkout.example.com/success', // Changed from redirectUrl to url
      status: 'open', // Changed from 'pending' to 'open'
    };
  }

  async retrieveCheckoutSession(id: string): Promise<CheckoutSession> {
    console.log('Mock: retrieveCheckoutSession', id);
    if (id === 'mock_cs_123') {
      return {
        id: 'mock_cs_123',
        clientSecret: 'mock_client_secret',
        url: 'https://mock-checkout.example.com/success', // Changed from redirectUrl to url
        status: 'complete',
      };
    }
    throw new PaymentsError(
      PaymentErrorType.CUSTOMER_NOT_FOUND,
      `Mock checkout session ${id} not found`
    );
  }

  async createCustomer(params: CustomerCreateParams): Promise<Customer> {
    console.log('Mock: createCustomer', params);
    const newCustomer: Customer = {
      id: `mock_cus_${this.nextCustomerId++}`,
      email: params.email,
      name: params.name || null,
      phone: params.phone || null,
      stripeCustomerId: null,
      subscriptions: [],
      defaultPaymentMethodId: null,
      paymentMethods: [], // Added paymentMethods
      metadata: params.metadata || {},
      created: new Date(),
      updated: new Date(),
    };
    this.customers.push(newCustomer);
    return newCustomer;
  }

  async retrieveCustomer(id: string): Promise<Customer> {
    console.log('Mock: retrieveCustomer', id);
    const customer = this.customers.find(c => c.id === id);
    if (!customer) {
      throw new PaymentsError(
        PaymentErrorType.CUSTOMER_NOT_FOUND,
        `Mock customer ${id} not found`
      );
    }
    return customer;
  }

  async updateCustomer(
    id: string,
    params: CustomerCreateParams
  ): Promise<Customer> {
    console.log('Mock: updateCustomer', id, params);
    const customer = this.customers.find(c => c.id === id);
    if (!customer) {
      throw new PaymentsError(
        PaymentErrorType.CUSTOMER_NOT_FOUND,
        `Mock customer ${id} not found`
      );
    }
    if (params.email) customer.email = params.email;
    if (params.name) customer.name = params.name;
    if (params.phone) customer.phone = params.phone;
    if (params.metadata)
      customer.metadata = { ...customer.metadata, ...params.metadata };
    customer.updated = new Date();
    return customer;
  }

  async deleteCustomer(id: string): Promise<void> {
    console.log('Mock: deleteCustomer', id);
    const initialLength = this.customers.length;
    this.customers = this.customers.filter(c => c.id !== id);
    if (this.customers.length === initialLength) {
      throw new PaymentsError(
        PaymentErrorType.CUSTOMER_NOT_FOUND,
        `Mock customer ${id} not found`
      );
    }
  }

  async listPaymentMethods(customerId: string): Promise<PaymentMethod[]> {
    console.log('Mock: listPaymentMethods', customerId);
    const customer = this.customers.find(c => c.id === customerId);
    if (!customer) {
      throw new PaymentsError(
        PaymentErrorType.CUSTOMER_NOT_FOUND,
        `Mock customer ${customerId} not found`
      );
    }
    return customer.paymentMethods || [];
  }

  async attachPaymentMethod(
    customerId: string,
    paymentMethodId: string
  ): Promise<PaymentMethod> {
    console.log('Mock: attachPaymentMethod', customerId, paymentMethodId);
    const customer = this.customers.find(c => c.id === customerId);
    if (!customer) {
      throw new PaymentsError(
        PaymentErrorType.CUSTOMER_NOT_FOUND,
        `Mock customer ${customerId} not found`
      );
    }
    const newPaymentMethod: PaymentMethod = {
      id: paymentMethodId,
      type: 'card',
      card: {
        brand: 'visa',
        last4: '4242',
        expMonth: 12,
        expYear: 2025,
        country: 'US',
      },
      isDefault: false,
      created: new Date(),
    };
    customer.paymentMethods.push(newPaymentMethod);
    return newPaymentMethod;
  }

  async detachPaymentMethod(
    customerId: string,
    paymentMethodId: string
  ): Promise<void> {
    console.log('Mock: detachPaymentMethod', customerId, paymentMethodId);
    const customer = this.customers.find(c => c.id === customerId);
    if (!customer) {
      throw new PaymentsError(
        PaymentErrorType.CUSTOMER_NOT_FOUND,
        `Mock customer ${customerId} not found`
      );
    }
    const initialLength = customer.paymentMethods.length;
    customer.paymentMethods = customer.paymentMethods.filter(
      (pm: PaymentMethod) => pm.id !== paymentMethodId
    );
    if (customer.paymentMethods.length === initialLength) {
      throw new PaymentsError(
        PaymentErrorType.CUSTOMER_NOT_FOUND,
        `Mock payment method ${paymentMethodId} not found for customer ${customerId}`
      );
    }
  }

  async createSubscription(
    params: SubscriptionCreateParams
  ): Promise<Subscription> {
    console.log('Mock: createSubscription', params);
    const customer = this.customers.find(c => c.id === params.customerId);
    if (!customer) {
      throw new PaymentsError(
        PaymentErrorType.CUSTOMER_NOT_FOUND,
        `Mock customer ${params.customerId} not found`
      );
    }
    const newSubscription: Subscription = {
      id: `mock_sub_${this.nextSubscriptionId++}`,
      customerId: params.customerId,
      status: SubscriptionStatus.ACTIVE,
      items: (
        params.items ||
        (params.priceId ? [{ priceId: params.priceId, quantity: 1 }] : [])
      ).map((item: { priceId: string; quantity?: number }) => ({
        id: `mock_sub_item_${Math.random().toString(36).substring(7)}`,
        priceId: item.priceId,
        quantity: item.quantity || 1,
      })),
      currentPeriodStart: new Date(),
      currentPeriodEnd: new Date(Date.now() + 30 * 24 * 60 * 60 * 1000), // 30 days from now
      cancelAtPeriodEnd: false,
      created: new Date(),
      updated: new Date(),
    };
    this.subscriptions.push(newSubscription);
    customer.subscriptions.push(newSubscription);
    return newSubscription;
  }

  async retrieveSubscription(id: string): Promise<Subscription> {
    console.log('Mock: retrieveSubscription', id);
    const subscription = this.subscriptions.find(s => s.id === id);
    if (!subscription) {
      throw new PaymentsError(
        PaymentErrorType.SUBSCRIPTION_NOT_FOUND,
        `Mock subscription ${id} not found`
      );
    }
    return subscription;
  }

  async updateSubscription(
    id: string,
    params: SubscriptionUpdateParams
  ): Promise<Subscription> {
    console.log('Mock: updateSubscription', id, params);
    const subscription = this.subscriptions.find(s => s.id === id);
    if (!subscription) {
      throw new PaymentsError(
        PaymentErrorType.SUBSCRIPTION_NOT_FOUND,
        `Mock subscription ${id} not found`
      );
    }
    if (params.cancelAtPeriodEnd !== undefined) {
      subscription.cancelAtPeriodEnd = params.cancelAtPeriodEnd;
      subscription.status = params.cancelAtPeriodEnd
        ? SubscriptionStatus.CANCELED
        : SubscriptionStatus.ACTIVE;
    }
    if (params.items) {
      subscription.items = params.items.map(
        (item: { priceId: string; quantity?: number }) => ({
          id: `mock_sub_item_${Math.random().toString(36).substring(7)}`,
          priceId: item.priceId,
          quantity: item.quantity || 1,
        })
      );
    }
    subscription.updated = new Date();
    return subscription;
  }

  async cancelSubscription(id: string): Promise<void> {
    console.log('Mock: cancelSubscription', id);
    const subscription = this.subscriptions.find(s => s.id === id);
    if (!subscription) {
      throw new PaymentsError(
        PaymentErrorType.SUBSCRIPTION_NOT_FOUND,
        `Mock subscription ${id} not found`
      );
    }
    subscription.status = SubscriptionStatus.CANCELED;
    subscription.cancelAtPeriodEnd = true;
    subscription.updated = new Date();
  }

  async reactivateSubscription(id: string): Promise<void> {
    console.log('Mock: reactivateSubscription', id);
    const subscription = this.subscriptions.find(s => s.id === id);
    if (!subscription) {
      throw new PaymentsError(
        PaymentErrorType.SUBSCRIPTION_NOT_FOUND,
        `Mock subscription ${id} not found`
      );
    }
    subscription.status = SubscriptionStatus.ACTIVE;
    subscription.cancelAtPeriodEnd = false;
    subscription.updated = new Date();
  }

  async listSubscriptions(
    customerId: string,
    status?: SubscriptionStatus
  ): Promise<Subscription[]> {
    console.log('Mock: listSubscriptions', customerId, status);
    const customer = this.customers.find(c => c.id === customerId);
    if (!customer) {
      throw new PaymentsError(
        PaymentErrorType.CUSTOMER_NOT_FOUND,
        `Mock customer ${customerId} not found`
      );
    }
    let subs = customer.subscriptions;
    if (status && status !== SubscriptionStatus.ALL) {
      subs = subs.filter(s => s.status === status);
    }
    return subs;
  }
}
